/* ==========================================================================
   STRYVE — smart sticky header
   - Hides on scroll down, reappears immediately on scroll up.
   - Horizon scrolls .page-wrapper at >=990px and the window below 990px, so
     scroll position is read from both and listeners are bound to both.
   - Never hides while a mega panel / the drawer is open or focus is inside.
   - Publishes --sv-annh / --sv-barh / --sticky-header-offset on <html> and
     --header-height / --header-group-height on <body> for Horizon sections.
   - Cart count follows Horizon's `shopify:cart:lines-update` standard event.
   ========================================================================== */
(function () {
  'use strict';

  var DOC = document.documentElement;
  var MQ_DESK = window.matchMedia('(min-width: 990px)');
  var MQ_RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  var HIDE_AFTER = 12; // px of continuous downward travel before hiding
  var SHOW_AFTER = 4; // px of upward travel before revealing ("immediately")
  var CART_LINES_UPDATE = 'shopify:cart:lines-update'; // StandardEvents.cartLinesUpdate

  function on(target, type, fn, opts) {
    if (target) target.addEventListener(type, fn, opts);
  }

  function init(root) {
    if (!root || root.__svHdr) return;

    var bar = root.querySelector('[data-sv-hdr-bar]');
    if (!bar) return;

    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    var sig = ac ? { signal: ac.signal } : false;
    var passive = ac ? { passive: true, signal: ac.signal } : { passive: true };

    var group = document.getElementById('header-group');
    var section = root.closest('.shopify-section');
    var pageWrapper = document.querySelector('.page-wrapper');
    var drawer = root.querySelector('[data-sv-drawer]');
    var scrim = root.querySelector('[data-sv-scrim]');
    var live = root.querySelector('[data-sv-cart-live]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-sv-item]'));
    var openers = Array.prototype.slice.call(root.querySelectorAll('[data-sv-drawer-open]'));

    var overlay = root.getAttribute('data-overlay') === 'true';
    var hideOnScroll = root.getAttribute('data-hide-on-scroll') === 'true';
    var hoverOpen = root.getAttribute('data-panel-hover') === 'true';

    var annH = 0;
    var barH = 0;
    var lastY = 0;
    var lastScrollAt = 0;
    var travel = 0;
    var hidden = false;
    var openItem = null;
    var openedByHoverAt = 0;
    var openTimer = 0;
    var closeTimer = 0;
    var drawerOpen = false;
    var weLocked = false;
    var lastFocus = null;
    var ro = null;

    /* ---------------- measurement ---------------- */
    // Clamped to the scroller's real range so iOS rubber-banding (negative / past-the-end
    // offsets) never reads as "scrolled up".
    function scrollY() {
      var w = window.scrollY || window.pageYOffset || 0;
      var p = pageWrapper ? pageWrapper.scrollTop : 0;
      var y;
      var max;
      if (p > w) {
        y = p;
        max = pageWrapper.scrollHeight - pageWrapper.clientHeight;
      } else {
        y = w;
        max = DOC.scrollHeight - window.innerHeight;
      }
      if (y < 0) y = 0;
      if (max > 0 && y > max) y = max;
      return y;
    }

    function publishOffset() {
      DOC.style.setProperty('--sticky-header-offset', (hidden ? 0 : barH) + 'px');
    }

    function measure() {
      annH = 0;
      if (group && section && group.contains(section)) {
        for (var el = group.firstElementChild; el && el !== section; el = el.nextElementSibling) {
          annH += el.offsetHeight;
        }
      }
      barH = bar.offsetHeight;
      DOC.style.setProperty('--sv-annh', annH + 'px');
      DOC.style.setProperty('--sv-barh', barH + 'px');
      document.body.style.setProperty('--header-height', barH + 'px');
      document.body.style.setProperty('--header-group-height', annH + barH + 'px');
      publishOffset();
    }

    /* ---------------- state ---------------- */
    // Keyboard focus only: a mouse click leaves focus on a nav button, and that must not pin the bar.
    function focusInsideBar() {
      try {
        return !!bar.querySelector(':focus-visible');
      } catch (e) {
        return false;
      }
    }

    function isPinned() {
      return drawerOpen || !!openItem || focusInsideBar() || DOC.hasAttribute('scroll-lock');
    }

    function syncOver() {
      var scrolled = root.classList.contains('is-scrolled');
      root.classList.toggle('is-over', overlay && !scrolled && !openItem && !drawerOpen);
    }

    function setHidden(next) {
      if (next === hidden) return;
      hidden = next;
      root.classList.toggle('is-hidden', next);
      publishOffset();
      if (next) closePanel(openItem, false);
    }

    function update() {
      var y = scrollY();
      var dy = y - lastY;
      lastY = y;
      if (dy !== 0) lastScrollAt = Date.now();

      root.classList.toggle('is-scrolled', y > annH + 2);
      syncOver();

      if (!hideOnScroll) return;

      if (isPinned() || y <= annH + barH) {
        travel = 0;
        setHidden(false);
        return;
      }

      if (dy > 0) {
        travel = travel > 0 ? travel + dy : dy;
        if (travel > HIDE_AFTER && y > annH + barH + 120) setHidden(true);
      } else if (dy < 0) {
        travel = travel < 0 ? travel + dy : dy;
        if (-travel > SHOW_AFTER) setHidden(false);
      }
    }

    // Scroll events are already dispatched once per rendered frame, and update() only toggles
    // classes after reading scroll offsets, so it runs inline (a rAF hop stalls in throttled tabs).
    function onScroll() {
      update();
    }

    /* ---------------- mega panels ---------------- */
    function triggerOf(item) {
      return item ? item.querySelector('[data-sv-trigger]') : null;
    }

    function openPanel(item, viaHover) {
      if (!item) return;
      clearTimeout(closeTimer);
      if (openItem && openItem !== item) closePanel(openItem, false);
      if (openItem === item) return;
      openItem = item;
      openedByHoverAt = viaHover ? Date.now() : 0;
      item.classList.add('is-open');
      var t = triggerOf(item);
      if (t) t.setAttribute('aria-expanded', 'true');
      root.classList.add('is-panel-open');
      setHidden(false);
      syncOver();
    }

    function closePanel(item, returnFocus) {
      if (!item) return;
      item.classList.remove('is-open');
      var t = triggerOf(item);
      if (t) t.setAttribute('aria-expanded', 'false');
      if (openItem === item) openItem = null;
      if (!openItem) root.classList.remove('is-panel-open');
      syncOver();
      if (returnFocus && t) t.focus({ preventScroll: true });
    }

    items.forEach(function (item) {
      var trigger = triggerOf(item);
      if (!trigger) return;

      on(trigger, 'click', function (event) {
        event.preventDefault();
        if (item.classList.contains('is-open')) {
          // A click right after a hover-open is the same intent — keep it open.
          if (openedByHoverAt && Date.now() - openedByHoverAt < 600) return;
          closePanel(item, false);
        } else {
          openPanel(item, false);
        }
      }, sig);

      on(trigger, 'keydown', function (event) {
        if (event.key === 'ArrowDown' && item.classList.contains('is-open')) {
          var first = item.querySelector('[data-sv-panel] a');
          if (first) {
            event.preventDefault();
            first.focus();
          }
        }
      }, sig);

      if (hoverOpen) {
        on(item, 'pointerenter', function (event) {
          if (event.pointerType !== 'mouse' || !MQ_DESK.matches) return;
          // Ignore the synthetic mouse move browsers fire when the bar slides in under a still cursor.
          if (Date.now() - lastScrollAt < 250) return;
          clearTimeout(closeTimer);
          clearTimeout(openTimer);
          openTimer = setTimeout(function () {
            openPanel(item, true);
          }, openItem ? 120 : 90);
        }, sig);

        on(item, 'pointerleave', function (event) {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(openTimer);
          closeTimer = setTimeout(function () {
            closePanel(item, false);
          }, 200);
        }, sig);
      }

      on(item, 'focusout', function (event) {
        if (event.relatedTarget && !item.contains(event.relatedTarget)) closePanel(item, false);
      }, sig);

      // Leaving via a panel link: close first so a back-navigation (bfcache) restores a clean page.
      var panel = item.querySelector('[data-sv-panel]');
      on(panel, 'click', function (event) {
        var a = event.target.closest && event.target.closest('a[href]');
        if (a) closePanel(item, false);
      }, sig);
    });

    on(document, 'click', function (event) {
      if (openItem && !openItem.contains(event.target)) closePanel(openItem, false);
    }, sig);

    // Keyboard users tabbing into a hidden bar get it back immediately.
    on(bar, 'focusin', function () {
      if (focusInsideBar()) setHidden(false);
    }, sig);

    /* ---------------- drawer ---------------- */
    function focusables(container) {
      return Array.prototype.filter.call(
        container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), summary, [tabindex]:not([tabindex="-1"])'),
        function (el) {
          return el.offsetParent !== null || el === document.activeElement;
        }
      );
    }

    function openDrawer() {
      if (!drawer || drawerOpen) return;
      closePanel(openItem, false);
      drawerOpen = true;
      lastFocus = document.activeElement;
      drawer.hidden = false;
      root.classList.add('is-drawer-open');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      if (!DOC.hasAttribute('scroll-lock')) {
        DOC.setAttribute('scroll-lock', '');
        weLocked = true;
      }
      setHidden(false);
      syncOver();
      bar.inert = true; // the drawer is modal: nothing in the bar is reachable behind it
      void drawer.offsetWidth; // commit the off-canvas start state so the slide-in transitions
      drawer.classList.add('is-open');
      var closeBtn = drawer.querySelector('[data-sv-drawer-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    function closeDrawer(returnFocus) {
      if (!drawer || !drawerOpen) return;
      drawerOpen = false;
      bar.inert = false;
      drawer.classList.remove('is-open');
      root.classList.remove('is-drawer-open');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (weLocked) {
        DOC.removeAttribute('scroll-lock');
        weLocked = false;
      }
      syncOver();

      var finished = false;
      function finish() {
        if (finished || drawerOpen) return;
        finished = true;
        drawer.hidden = true;
      }
      if (MQ_RM.matches) {
        finish();
      } else {
        on(drawer, 'transitionend', function handler(event) {
          if (event.target !== drawer) return;
          drawer.removeEventListener('transitionend', handler);
          finish();
        });
        setTimeout(finish, 650);
      }

      if (returnFocus && lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    openers.forEach(function (b) {
      on(b, 'click', function () {
        if (drawerOpen) closeDrawer(true);
        else openDrawer();
      }, sig);
    });

    if (drawer) {
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-sv-drawer-close]'), function (b) {
        on(b, 'click', function () { closeDrawer(true); }, sig);
      });

      on(drawer, 'keydown', function (event) {
        if (event.key !== 'Tab') return;
        var list = focusables(drawer);
        if (!list.length) return;
        var first = list[0];
        var last = list[list.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, sig);

      // Following a link inside the drawer: close it so bfcache restores a clean page.
      on(drawer, 'click', function (event) {
        var a = event.target.closest && event.target.closest('a[href]');
        if (a) closeDrawer(false);
      }, sig);
    }

    on(scrim, 'click', function () {
      if (drawerOpen) closeDrawer(true);
      closePanel(openItem, false);
    }, sig);

    on(document, 'keydown', function (event) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (drawerOpen) {
        event.preventDefault();
        closeDrawer(true);
      } else if (openItem) {
        event.preventDefault();
        closePanel(openItem, openItem.contains(document.activeElement));
      }
    }, sig);

    /* ---------------- search & cart ---------------- */
    Array.prototype.forEach.call(root.querySelectorAll('[data-sv-search]'), function (a) {
      on(a, 'click', function (event) {
        var modal = document.getElementById('search-modal');
        if (modal && typeof modal.showDialog === 'function') {
          event.preventDefault();
          closeDrawer(false);
          closePanel(openItem, false);
          try {
            modal.showDialog();
          } catch (err) {
            window.location.href = a.href;
          }
        }
      }, sig);
    });

    var counts = Array.prototype.slice.call(root.querySelectorAll('[data-sv-cart-count]'));
    var cartLinks = Array.prototype.slice.call(root.querySelectorAll('[data-sv-cart]'));

    // The Liquid only announces a popup for Horizon's drawer (cart_type = drawer), which Stryve turns off;
    // our drawer is a modal <dialog> (absent on the cart template, where the link just navigates).
    if (document.querySelector('[data-sv-cart-dialog]')) {
      cartLinks.forEach(function (a) {
        if (!a.hasAttribute('aria-haspopup')) a.setAttribute('aria-haspopup', 'dialog');
      });
    }

    cartLinks.forEach(function (a) {
      on(a, 'click', function (event) {
        var S = window.Stryve;
        var d = document.getElementById('cart-drawer');
        var canDrawer = (S && typeof S.openCart === 'function') || (d && typeof d.open === 'function');
        if (!canDrawer) return; // plain navigation to /cart
        event.preventDefault();
        closeDrawer(false);
        closePanel(openItem, false);
        try {
          if (S && typeof S.openCart === 'function') S.openCart();
          else d.open();
        } catch (err) {
          window.location.href = a.href;
        }
      }, sig);
    });

    function setCount(n, announce) {
      if (typeof n !== 'number' || isNaN(n)) return;
      var changed = false;
      counts.forEach(function (el) {
        if (el.textContent.trim() !== String(n)) changed = true;
        el.textContent = String(n);
        el.hidden = n <= 0;
        if (changed && n > 0 && !MQ_RM.matches) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        }
      });
      cartLinks.forEach(function (a) {
        a.setAttribute('aria-label', 'Bag, ' + n + (n === 1 ? ' item' : ' items'));
      });
      if (announce && changed && live) {
        live.textContent = 'Bag updated: ' + n + (n === 1 ? ' item' : ' items');
      }
    }

    function refreshCount() {
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(base + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) setCount(c.item_count, true); })
        .catch(function () {});
    }

    // Horizon (v4+) dispatches Shopify's standard CartLinesUpdateEvent before the request settles;
    // its promise resolves with { cart: { totalQuantity } }.
    on(document, CART_LINES_UPDATE, function (event) {
      var p = event && event.promise;
      if (!p || typeof p.then !== 'function') {
        refreshCount();
        return;
      }
      p.then(
        function (res) {
          var q = res && res.cart && res.cart.totalQuantity;
          if (typeof q === 'number') setCount(q, true);
          else refreshCount();
        },
        function (err) {
          if (!err || err.name !== 'AbortError') refreshCount();
        }
      );
    }, sig);

    // Legacy / custom events (older Horizon, and our own cart drawer in Phase 4)
    function onCartEvent(event) {
      var d = event && event.detail;
      var n = d && d.resource && typeof d.resource.item_count === 'number' ? d.resource.item_count : null;
      if (n === null && d && typeof d.item_count === 'number') n = d.item_count;
      if (n !== null) setCount(n, true);
      else refreshCount();
    }

    on(document, 'cart:update', onCartEvent, sig);
    on(document, 'sv:cart:update', onCartEvent, sig);

    on(window, 'pageshow', function (event) {
      if (!event.persisted) return;
      refreshCount();
      closeDrawer(false);
      closePanel(openItem, false);
      lastY = scrollY();
      travel = 0;
      update();
    }, sig);

    /* ---------------- wiring ---------------- */
    on(window, 'scroll', onScroll, passive);
    on(pageWrapper, 'scroll', onScroll, passive);

    function onBreakpoint() {
      if (MQ_DESK.matches) closeDrawer(false);
      else closePanel(openItem, false);
      lastY = scrollY();
      travel = 0;
      measure();
      update();
    }

    if (MQ_DESK.addEventListener) MQ_DESK.addEventListener('change', onBreakpoint, sig);
    else if (MQ_DESK.addListener) MQ_DESK.addListener(onBreakpoint);

    function observe() {
      if (!ro) return;
      ro.disconnect();
      ro.observe(bar);
      if (group) {
        for (var el = group.firstElementChild; el && el !== section; el = el.nextElementSibling) ro.observe(el);
      }
    }

    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () { measure(); });
      observe();
    } else {
      on(window, 'resize', measure, passive);
    }

    root.__svHdr = {
      destroy: function () {
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
        if (ac) ac.abort();
        if (ro) ro.disconnect();
        if (weLocked) {
          DOC.removeAttribute('scroll-lock');
          weLocked = false;
        }
        bar.inert = false;
        root.__svHdr = null;
      },
      // Theme editor: sibling header-group sections (the announcement bar) get replaced on re-render.
      remeasure: function () {
        observe();
        measure();
        update();
      },
      reveal: function () {
        setHidden(false);
      },
      openFor: function (el) {
        var item = el && el.closest && el.closest('[data-sv-item]');
        if (item && root.contains(item)) openPanel(item, false);
      },
      closePanels: function () {
        closePanel(openItem, false);
      },
    };

    measure();
    lastY = scrollY();
    update();
  }

  function eachHeader(fn) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sv-hdr]'), function (r) {
      if (r.__svHdr) fn(r.__svHdr);
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sv-hdr]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ---------------- theme editor ---------------- */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    var root = target && target.querySelector && target.querySelector('[data-sv-hdr]');
    if (root) {
      init(root);
      return;
    }
    if (target && target.closest && target.closest('#header-group')) {
      eachHeader(function (h) { h.remeasure(); });
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target && event.target.querySelector && event.target.querySelector('[data-sv-hdr]');
    if (root && root.__svHdr) root.__svHdr.destroy();
  });

  document.addEventListener('shopify:section:select', function (event) {
    var root = event.target && event.target.querySelector && event.target.querySelector('[data-sv-hdr]');
    if (root && root.__svHdr) root.__svHdr.reveal();
  });

  // Selecting a mega-menu promo block opens the panel it lives in, so it is visible while editing.
  document.addEventListener('shopify:block:select', function (event) {
    var el = event.target;
    if (!el || !el.closest || !el.closest('[data-sv-hdr]')) return;
    var root = el.closest('[data-sv-hdr]');
    if (root.__svHdr) {
      root.__svHdr.reveal();
      root.__svHdr.openFor(el);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var el = event.target;
    var root = el && el.closest && el.closest('[data-sv-hdr]');
    if (root && root.__svHdr) root.__svHdr.closePanels();
  });
})();
